import {Component, effect, inject, Injectable, input} from '@angular/core';
import {ActivatedRoute, Params, RouterOutlet} from '@angular/router';
import {map} from 'rxjs';
import {toSignal} from '@angular/core/rxjs-interop';
import {PortfolioDataContext} from './portfolio-data-context.service';
import {PortfolioSegmentDataContext} from './portfolio-segment-data-context.service';

/**
 * PortfolioContextResolverComponent is responsible for resolving and activating
 * the context for a specific portfolio based on the portfolio ID obtained from
 * the route parameters. This component ensures that the necessary portfolio data
 * and associated segment data are loaded and activated when the component is initialized.
 */
@Component({
    selector: 'app-portfolio-context-resolver-component',
    template: '<router-outlet></router-outlet>',
    imports: [RouterOutlet],
})
export class PortfolioContextResolverComponent {

    private readonly route = inject(ActivatedRoute);
    private readonly portfolioId = toSignal(
        this.route.params.pipe(map((res: Params) => res['pfid']))
    );

    private readonly pfData = inject(PortfolioDataContext);
    private readonly pfSegData = inject(PortfolioSegmentDataContext);

    constructor() {
        effect(async () => await this.resolvePortfolioContext(this.portfolioId()));
    }

    private async resolvePortfolioContext(portfolioId: string | undefined): Promise<void> {
        if (!portfolioId) {
            throw new Error('Portfolio ID is required');
        } else {
            await this.pfData.activatePortfolioById(portfolioId);
            await this.pfSegData.activatePortfolioById(portfolioId);
        }
    }
}

@Injectable()
export class ChildApiService {
    constructor() {
    }
}

/**
 * Represents a component responsible for resolving and activating a portfolio segment context.
 *
 * This component is designed to handle the retrieval of portfolio and segment IDs
 * from the route parameters, and subsequently activate the corresponding portfolio
 * segment context using the `PortfolioSegmentDataContext` service.
 *
 * - Extracts portfolio and segment IDs from the route parameters.
 * - Activates a portfolio segment context based on the extracted IDs.
 *
 */
@Component({
    selector: 'app-portfolio-segment-context-resolver-component',
    template: '<router-outlet></router-outlet>',
    imports: [RouterOutlet],
    providers: [
        {
            provide: ChildApiService,
            useFactory: (component: PortfolioSegmentContextResolverComponent) => component.createChildApiService(),
            deps: [PortfolioSegmentContextResolverComponent]
        }
    ]
})
export class PortfolioSegmentContextResolverComponent {

    private readonly route = inject(ActivatedRoute);

    private readonly pfSegData = inject(PortfolioSegmentDataContext);
    readonly pfid = input<string | undefined>();
    readonly segmentId = input<string | undefined>();

    constructor() {
        effect(() => this.resolveSegmentContextByIds(this.pfid(), this.segmentId()));
    }

    // Factory method to create ChildApiService instances
    async createChildApiService(): Promise<ChildApiService> {
        console.log('Creating ChildApiService instance', this.segmentId());
        return new Promise<ChildApiService>((resolve, reject) => {
            return setTimeout(() => resolve(new ChildApiService()), 10_000);
        });
    }

    private async resolveSegmentContextByIds(portfolioId: string | undefined, segmentId: string | undefined): Promise<void> {
        if (!portfolioId || !segmentId) {
            throw new Error('Portfolio ID and segment ID are required');
        } else {
            try {
                const key = `activate-portfolio-segment-${portfolioId}-${segmentId}`;
                await navigator.locks.request(key, async (lock) => {
                    await this.pfSegData.activatePortfolioSegmentByIds(portfolioId, segmentId);
                });
            } catch (error) {
                console.error('Failed to activate portfolio segment context:', error);
            }
        }
    }
}
